import React from 'react';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';

import ExpandedRunDetails from './ExpandedRunDetails';

const RunRow = ({ controller, run, absoluteIdx, runRowKey }) => {
  const {
    expandedRun,
    getRunConfidencePercent,
    getRunPointStats,
    getRunTokenTotals,
    getRunDataPointsHint,
    getRunFilteredAccuracy,
    formatRunDataPoints,
    toFiniteNumber,
    toFiniteCount,
    formatTokenCount,
    formatResizeDisplay,
    toBool,
    formatCostDisplay,
    loadingRunDetailsById,
    handleRunRowClick,
    handleRunExport,
  } = controller;

  const isExpanded = expandedRun === runRowKey;
  const runConfidencePercent = getRunConfidencePercent(run, runRowKey);
  const runMinConfidenceThreshold = runConfidencePercent / 100;
  const baseRunPointStats = getRunPointStats(run);
  const runTokenTotals = getRunTokenTotals(run);
  const runDataPointsHint = getRunDataPointsHint(run);
  const filteredRunAccuracy = getRunFilteredAccuracy(run, runMinConfidenceThreshold);
  const runDataPointsDisplay = formatRunDataPoints(run);
  const displayAvgSpeedMs = toFiniteNumber(run.avg_speed_ms ?? run.avg_speed);
  const runId = String(run?.run_id || '').trim();
  const hasLoadedRunResults = Array.isArray(run?.results);
  const hasActiveAccuracyFilter = runMinConfidenceThreshold > 0;
  const isRunDetailsLoading = Boolean(loadingRunDetailsById[runId]);
  const isAccuracyRecalcPending = hasActiveAccuracyFilter && !hasLoadedRunResults && Boolean(runId);
  const displayAvgAccuracy = filteredRunAccuracy !== null
    ? filteredRunAccuracy
    : (hasActiveAccuracyFilter && hasLoadedRunResults)
      ? null
      : (isAccuracyRecalcPending ? null : toFiniteNumber(run.avg_accuracy));
  const displayRunTotalCost = toFiniteNumber(run.total_cost);
  const canExportRun = hasLoadedRunResults ? run.results.length > 0 : Boolean(runId);
  const attempted = baseRunPointStats.attempted;
  const successful = baseRunPointStats.successful;
  const skipped = baseRunPointStats.skipped;

  return (
    <React.Fragment>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={() => handleRunRowClick(runRowKey, run)}
      >
        <td className="p-4 text-sm text-slate-600">
          <div className="min-w-[86px]">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <button
                type="button"
                onClick={(event) => handleRunExport(event, run, absoluteIdx)}
                disabled={!canExportRun || isRunDetailsLoading}
                className={`inline-flex items-center rounded border px-1.5 py-1 text-[11px] font-semibold transition-colors ${(canExportRun && !isRunDetailsLoading) ? 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                title={!canExportRun ? 'Run details are unavailable for export.' : (hasLoadedRunResults ? 'Export this test run to CSV' : 'Load run details and export to CSV')}
                aria-label="Export run to CSV"
              >
                <Download size={12} className="mr-1" />
                {isRunDetailsLoading ? 'Loading...' : 'CSV'}
              </button>
            </div>
          </div>
        </td>
        <td className="p-4 text-sm text-slate-600">{new Date(run.timestamp).toLocaleString()}</td>
        <td className="p-4 text-sm text-slate-600">{run.model || 'N/A'}</td>
        <td className="p-4 text-sm text-slate-600">{formatResizeDisplay(run.resize_mode)}</td>
        <td className="p-4 text-sm text-slate-600">{run.compression_mode || 'N/A'}</td>
        <td className="p-4 text-sm text-slate-600">{toBool(run.use_ebay_rag, false) ? 'On' : 'Off'}</td>
        <td className="p-4 text-sm text-slate-600">{toBool(run.use_notes_rag, false) ? 'On' : 'Off'}</td>
        <td className="p-4 text-sm text-slate-600">{run.dataset_name || run.dataset_prefix || 'Container root'}</td>
        <td className="p-4 text-sm">
          <span className={`px-2 py-1 rounded-full text-xs font-bold ${run.mode === 'MOCK' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
            {run.mode || 'UNKNOWN'}
          </span>
        </td>
        <td className="p-4 text-sm text-slate-600" title={runDataPointsHint || undefined}>
          {runDataPointsDisplay}
        </td>
        <td className="p-4 text-sm text-slate-600">
          {displayAvgSpeedMs !== null ? `${displayAvgSpeedMs.toFixed(0)} ms` : 'N/A'}
        </td>
        <td className="p-4 text-sm font-bold text-slate-700">
          {isAccuracyRecalcPending ? (
            <span className="inline-flex items-center rounded bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
              Loading...
            </span>
          ) : (displayAvgAccuracy !== null ? `${(displayAvgAccuracy * 100).toFixed(1)}%` : 'N/A')}
        </td>
        <td className="p-4 text-sm text-slate-600">
          <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${toFiniteCount(runTokenTotals.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
            {formatTokenCount(runTokenTotals.input)}
          </span>
        </td>
        <td className="p-4 text-sm text-slate-600">
          <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${toFiniteCount(runTokenTotals.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
            {formatTokenCount(runTokenTotals.output)}
          </span>
        </td>
        <td className="p-4 text-sm text-slate-600">{formatCostDisplay(displayRunTotalCost)}</td>
      </tr>
      {isExpanded && (
        <tr className="bg-slate-50 border-b border-slate-100">
          <td colSpan="15" className="p-4">
            <ExpandedRunDetails
              controller={controller}
              run={run}
              runRowKey={runRowKey}
              attempted={attempted}
              successful={successful}
              skipped={skipped}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default RunRow;
